/**
 * @fileoverview Hook de Data Layer para estructura de menús contextuales de conversaciones del inbox
 * @module useInboxChatListContextMenuData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 3.1.0
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useInboxFilterOptions from "./useInboxFilterOptions";
import STATUS_CHAT_MAP from '../../../utils/statusChatMap';
import { getCommonAllowedStateIds, isStateOptionDisabled } from '../../../utils/chatStateOptions';

/**
 * Custom hook that returns the context menu structure according to configuration.
 *
 * Main function that implements the Data Layer logic to generate
 * the complete context menu structure of conversations.
 *
 * @function useInboxChatListContextMenuData
 * @memberof useInboxChatListContextMenuData
 * @export default
 *
 * @description
 * **Implementation of the Data Layer:**
 * - Configure base structure of the context menu
 * - Integrate agent options for transfer submenu
 * - Apply dynamic translations to all elements
 * - Optimize re-creation through memoization
 *
 * **Data Flow:**
 * 1. Get translations from the InboxMC namespace
 * 2. Consume agentListOptions from the centralized Data Layer
 * 3. Build menu structure with base elements
 * 4. Generate dynamic transfer submenu
 * 5. Configure icons and visual states
 * 6. Return memoized structure
 */
export default function useInboxChatListContextMenuData() {
  const { t } = useTranslation('InboxMC');
  const { agentListOptions } = useInboxFilterOptions();
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser || {});
  const canAssignChatsToAgents = Boolean(currentRetailerUser?.can_assign_chats_to_agents);

  return useMemo(() => (
    (conversation, targetConversations = [conversation]) => {
      const statusChatValues = (Array.isArray(targetConversations) && targetConversations.length > 0
        ? targetConversations
        : [conversation]
      ).map((targetConversation) => Number(targetConversation?.statusChat));
      const availableStateIds = getCommonAllowedStateIds(statusChatValues)
        .filter((stateId) => !isStateOptionDisabled(statusChatValues, stateId));

      const markAsStateItems = availableStateIds.map((stateId) => ({
        key: `markState_${stateId}`,
        stateId,
        label: t('chat_list.context_menu.mark_as_state', {
          state: t(`header.state_${STATUS_CHAT_MAP[stateId]?.value}`)
        })
      }));

      const items = [
        {
          key: 'transfer',
          label: t('chat_list.context_menu.transfer'),
          subItems: agentListOptions.map((option) => ({
            key: option.id,
            label: option.label,
            icon: 'dot-filled',
            iconClassName: option.active ? 'tw-text-green-400' : 'tw-text-gray-200',
            disabled: !canAssignChatsToAgents && Number(option.id) !== Number(currentRetailerUser?.id),
            disabledTitle: !canAssignChatsToAgents && Number(option.id) !== Number(currentRetailerUser?.id)
              ? t('header.assign_permission_blocked_notice')
              : ''
          }))
        },
        { key: 'markUnread', label: t('chat_list.context_menu.mark_unread') },
        ...markAsStateItems,
        { separator: true },
        { key: 'select', label: t('chat_list.context_menu.select') }
      ];

      return items;
    }
  ), [agentListOptions, canAssignChatsToAgents, currentRetailerUser?.id, t]);
}
